<script setup lang="ts">
import type { PropType } from "vue";
import { useStore } from "vuex";
import { computed } from "vue";
import type { OptionContent } from "@aion-dk/js-client/dist/lib/av_client/types";
import i18n from "../../../entrypoints/shared/i18n";
import breakpoint from "../../../entrypoints/shared/breakpoint";
import {
  optionPosition,
  optionColorStyle,
  optionColorClass,
  optionTitle
} from "../../../entrypoints/shared/handraise_helpers";
import { flattenOptions } from "../../../entrypoints/shared/contest_utilities";

const props = defineProps({
  options: {
    type: Array as PropType<OptionContent []>,
    required: true,
  },
  observerMode: {
    type: Boolean,
    default: false,
  },
  customStyle: {
    type: String,
    default: null,
  },
});

const { tc } = i18n.global;

const store = useStore();

const locale = computed(() => i18n.global.locale);

const options = computed(() => flattenOptions(store.getters.contestsInActiveVotingRound[0].options));

const blankAccentColor = computed(() => store.getters.contestsInActiveVotingRound[0].blankOptionColor);

const showPercentages = computed(() => store.getters.contestsInActiveVotingRound[0].resultPercentages);

const disregardBlankVotes = computed(() => store.getters.contestsInActiveVotingRound[0].disregardBlankVotes);

const handRaiseLiveResults = computed(() => store.getters.handRaiseLiveResults);

const maxWidth = computed(() => {
  if (breakpoint.greaterEquals("qhd")) return `max-width: 18rem;`;
  if (breakpoint.greater("lg") && breakpoint.lessEquals("qhd")) return `max-width: 14rem;`;
  return `max-width: 8.5rem;`;
});
</script>

<template>
  <section class="d-flex justify-content-center mt-2 mb-4 gap-3 flex-wrap">
    <div
      v-for="(optionReference, index) in Object.keys(handRaiseLiveResults)"
      :key="`reference-for-${optionReference}`"
      class="d-flex p-2 align-items-center gap-2"
      :class="{ border: observerMode }"
      :style="`${optionPosition(options, optionReference, index)} ${customStyle && observerMode ? customStyle : ''}`"
      v-tooltip="optionTitle(options, optionReference, locale)"
    >
      <div
        :class="`option-color-badge ${optionColorClass(optionReference, blankAccentColor)}`"
        :style="optionColorStyle(options, optionReference, blankAccentColor)"
      />
      <span
        class="text-truncate"
        :style="customStyle && observerMode ? maxWidth : `max-width: 8.5rem;`"
      >
        {{ optionTitle(options, optionReference, locale) }}
      </span>

      <div v-if="observerMode" class="d-flex gap-2 ms-3">
        <span class="text-nowrap fw-bold">
          {{ tc("js.handraise.list_view.votes", handRaiseLiveResults[optionReference].results.count) }}
        </span>
        <span v-if="((handRaiseLiveResults[optionReference].results.percentage && optionReference !== 'blank') || (optionReference === 'blank' && !disregardBlankVotes)) && showPercentages" class="text-nowrap fw-bold">
          {{ handRaiseLiveResults[optionReference].results.percentage }}%
        </span>
      </div>
    </div>
  </section>
</template>
